import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Link from '@mui/material/Link';
import { useState } from 'react';

import { FileAttachmentBasic } from '~hooks/useFileAttachment/models';
import AttachmentCard, {
  attachmentCardSx,
} from '~pages/Messages/components/AttachmentCard';
import ImageLightbox from '~pages/Messages/components/ImageLightbox';
import formatByteSize from '~utils/formatByteSize';

type MessageAttachmentsProps = {
  attachments: FileAttachmentBasic[];
};

/**
 * A message's attachments inside its bubble: one card per file, naming it and its size. An
 * image puts its own preview in the card's tile and opens a lightbox that steps through the
 * message's other images.
 */
export default function MessageAttachments({ attachments }: MessageAttachmentsProps) {
  const theme = useTheme();
  const [openedImageIndex, setOpenedImageIndex] = useState<number | null>(null);

  if (attachments.length === 0) {
    return null;
  }

  const images = attachments.filter((attachment) => attachment.isImage);
  const lightboxImages = images.map((image) => ({
    downloadUrl: image.downloadUrl,
    fileName: image.fileName,
    imageUrl: image.fileUrl,
  }));

  return (
    <Box
      data-test-id="messaging-message-attachments"
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.875, pt: 0.875 }}
    >
      {attachments.map((attachment) =>
        attachment.isImage ? (
          <ButtonBase
            data-test-id="messaging-attachment-thumbnail"
            key={attachment.id}
            onClick={() => setOpenedImageIndex(images.indexOf(attachment))}
            sx={attachmentCardSx(theme)}
          >
            <AttachmentCard
              fileName={attachment.fileName}
              imageUrl={attachment.previewUrl}
              isImage
              secondaryText={formatByteSize(attachment.byteSize)}
            />
          </ButtonBase>
        ) : (
          <Link
            data-test-id="messaging-attachment-file"
            href={attachment.fileUrl}
            key={attachment.id}
            rel="noopener"
            sx={attachmentCardSx(theme)}
            target="_blank"
          >
            <AttachmentCard
              fileName={attachment.fileName}
              isImage={false}
              secondaryText={formatByteSize(attachment.byteSize)}
            />
          </Link>
        ),
      )}

      {openedImageIndex !== null && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={openedImageIndex}
          isOpen
          onClose={() => setOpenedImageIndex(null)}
        />
      )}
    </Box>
  );
}
